/**
 * Build a `localStorage` key scoped to the active organization and, where
 * resolved, workspace — so switching org or workspace can never resurrect
 * another tenant's cached UI state (filters, saved views, drafts, ...).
 *
 * `scope: "workspace"` is the right default for anything read on a
 * workspace-scoped route (see `ORGANIZATION_ROUTE_ROOTS` in
 * `workspaceRoutes.ts`) — that covers most product surfaces (conversations,
 * runs, dashboard, ...). Use `scope: "organization"` only for state that
 * genuinely lives on an organization-global route (e.g. Settings).
 *
 * Returns `undefined` only when the organization itself isn't resolved yet —
 * callers should treat that as "don't persist yet" rather than falling back
 * to an unscoped key, which is the bug this exists to prevent. A `"workspace"`
 * request with no resolved workspace id degrades to the organization-scoped
 * key instead of `undefined`: `WorkspaceProviderRoute` deliberately supplies
 * an empty workspace context for an organization with the Workspaces feature
 * disabled (not just transiently while loading — that's the steady state for
 * those organizations), and returning `undefined` there would silently drop
 * persistence entirely rather than degrade. Org scope still isolates by
 * organization (the leak this module exists to close), and a caller built on
 * `useFilterParams`'s rescope-on-key-change re-hydrates under the more
 * specific org+workspace key the moment a workspace id does resolve.
 */
export type StorageScope = "organization" | "workspace"

interface ScopedStorageIds {
  organizationId: string | null | undefined
  workspaceId?: string | null
}

export function buildScopedStorageKey(
  base: string,
  scope: StorageScope,
  ids: ScopedStorageIds,
): string | undefined {
  const { organizationId, workspaceId } = ids

  if (!organizationId) {
    return undefined
  }
  if (scope === "organization" || !workspaceId) {
    return `${base}:org_${organizationId}`
  }
  return `${base}:org_${organizationId}:ws_${workspaceId}`
}
